










































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import DaysComparatorChart, {
  ComparatorChartFilter,
} from "@/components/Investor/DaysComparatorChart.vue";
import MediaValue from "@/interfaces/MediaValue";
import SelectOption from "@/interfaces/SelectOption";
import VMultiSwitcher from "@/components/VMultiSwitcher.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import VCheckButton from "@/components/VCheckButton.vue";
import { AdcontrolProfile, Media, User } from "@/models";
import HourInlineComparisonHelper from "@/services/HourInlineComparisonHelper";
import NamedWithTimeIntervals from "@/models/interfaces/NamedWithTimeIntervals";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import NoCache from "@/decorators/NoCache";
import { ProfileCapabilitiesService } from "@/services/Profile/CapabilitiesService";
import { Getter } from "vuex-class";
import ClientUserInfo from "@/models/Agents/UserInfo";

Component.registerHooks(["beforeRouteUpdate"]);

const comparisonsInlineRequestTag = "inline_comparisons";

// eslint-disable-next-line
type IFormData = any;

@Component({
  components: {
    DaysComparatorChart,
    VMultiSwitcher,
    VSwitchButton,
    VCheckButton,
    MultiLevelSelect,
  },
})
export default class DaysComparationsCard extends Vue {
  @Prop() readonly spotIds?: string[];
  @Prop({ required: true }) readonly entity!: { id: string; type: string };
  @Prop({ required: true }) readonly filter!: ComparatorChartFilter;
  @Prop({ required: true }) readonly media!: MediaValue;
  @Prop({ default: "spots" }) readonly unitOfMeasure!: string;
  @Prop({ required: true })
  readonly unitOfMeasureOptions!: Array<MultiLevelObject>;
  @Prop({ required: true }) readonly getData!: (
    data: IFormData,
    tag: string
  ) => Promise<NamedWithTimeIntervals[]>;
  @Prop({ default: undefined }) readonly grpTargetId!: string;
  @Prop({ required: true }) readonly adcontrolProfile!: AdcontrolProfile;

  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Getter("agentUserInfos") readonly agentUserInfo?: ClientUserInfo;
  @Getter("user") readonly user!: User;

  private loading = false;
  private weekdaysItems: NamedWithTimeIntervals[] = [];
  private hoursItems: NamedWithTimeIntervals[] = [];

  charts = {
    weekdays: {
      percentage: false,
      type: "bar",
      stacked: false,
    },
    hours: {
      percentage: false,
      type: "line",
      stacked: false,
    },
  };

  settings = {
    unitOfMeasure: this.unitOfMeasure,
  };

  mounted() {
    this.settings.unitOfMeasure = this.unitOfMeasure;
    this.loadComparisonsInline();
  }

  @Watch("filter", { deep: true })
  onDaysComparationsFilter() {
    this.loadComparisonsInline();
  }

  @Watch("unitOfMeasure")
  onUnitChanged(value: string) {
    this.settings.unitOfMeasure = value;
  }

  @Watch("adcontrolProfile")
  onProfileChanged() {
    this.loadComparisonsInline();
  }

  @Watch("grpTargetId")
  async loadComparisonsInline() {
    const { type, id } = this.entity;
    this.loading = true;
    const date = this.filter.date;

    const grpTarget = this.grpTargetId;

    const media = Media.mediaToList(this.filter.media);

    const data: IFormData = {
      filter: { media, date_range: [date.from, date.to], grpTarget },
      granularity: "wday",
      group: {},
    };
    if (this.grpTargetId) {
      data.grpTarget = this.grpTargetId;
    }
    data.group[type] = [id];
    if (this.spotIds !== undefined) {
      data.filter.ids = this.spotIds;
    }
    if (this.applyAgentsFilter) {
      data.agent_module_view = true;
    }

    const [weekdayItems, hoursItems] = await this.getWeekAndHours(data, {
      ...data,
      granularity: "hour",
    });

    this.weekdaysItems = HourInlineComparisonHelper.ReduceData(weekdayItems);
    this.hoursItems = HourInlineComparisonHelper.SlideData(
      HourInlineComparisonHelper.ReduceData(hoursItems),
      6
    );

    this.loading = false;
  }

  getWeekAndHours(weekData: IFormData, hoursData: IFormData) {
    return Promise.all([
      this.getData(weekData, comparisonsInlineRequestTag),
      this.getData(hoursData, comparisonsInlineRequestTag),
    ]);
  }

  get weekdaysTVColor() {
    return "#f75151";
  }

  get hoursTVColor() {
    return "#4b5eef";
  }

  get weekdaysRadioColor() {
    return "#ff9d00";
  }

  get hoursRadioColor() {
    return "#33c8ff";
  }

  get unitOfMeasureName() {
    switch (this.unitOfMeasure) {
      case "seconds":
        return "Secondi";
      case "spots":
        return "N° Spot";
      case "grp":
        return "GRP";
      case "audience":
        return "Contatti";
      case "ebiquity_min":
        return "€ (Stima Min.)";
      case "ebiquity_max":
        return "€ (Stima Max.)";
      default:
        return "Dati";
    }
  }

  @NoCache
  get anyData() {
    const anyHoursData = this.hoursItems.some((item) => {
      return item.timeIntervals.some((interval) => {
        return interval.detectionsCount > 0;
      });
    });
    const anyWeekdaysData = this.weekdaysItems.some((item) => {
      return item.timeIntervals.some((interval) => {
        return interval.detectionsCount > 0;
      });
    });
    return anyHoursData || anyWeekdaysData;
  }

  get applyAgentsFilter(): boolean {
    return (
      ProfileCapabilitiesService.agentsModuleEnabled(
        this.user.profile,
        this.agentUserInfo
      ) && !(this.agentUserInfo?.superUser ?? false)
    );
  }

  @Watch("settings", { deep: true, immediate: true })
  onSettingsChanged(value: { unitOfMeasure: string }) {
    this.$emit("settings-changed", value);
  }

  private chartTypeItems: Array<SelectOption> = [
    { id: "bar", text: "Barre" },
    { id: "line", text: "Linea" },
  ];
}
